import React, { useCallback, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Stack,
} from '@mui/material';
import { observer } from 'mobx-react';
import { markdownTable } from 'markdown-table';
import { notificationStore } from '../store/Notification';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export const TableJobsOverview = observer(
  ({
    data,
    title,
    showCopyButton = true,
    showDownloadCsvButton = false,
  }: {
    data: Array<Record<string, unknown>>;
    title?: string;
    showCopyButton?: boolean;
    showDownloadCsvButton?: boolean;
  }) => {
    const [copied, setCopied] = useState(false);

    const handleCopyMarkdown = useCallback(async () => {
      const headers = Object.keys(data[0]);
      const rows = data.map((item) =>
        headers.map((key) => String(item[key] ?? ''))
      );
      const mdString = markdownTable([headers, ...rows]);

      try {
        await navigator.clipboard.writeText(mdString);
        setCopied(true);
        setTimeout(() => setCopied(false), 5000);
      } catch (err) {
        notificationStore.showMessage(
          'Error during copy to clipboard',
          'error'
        );
      }
    }, [data]);

    const handleDownloadCsv = useCallback(() => {
      if (!data?.length) {
        return;
      }

      const headers = Object.keys(data[0]);
      const rows = data.map((item) => headers.map((key) => item[key]));

      const escapeCsvCell = (value: any) => {
        if (value === null || value === undefined) {
          return '';
        }

        const cell = String(value);
        if (/[",\n]/.test(cell)) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      };

      const csvContent = [headers, ...rows]
        .map((row) => row.map(escapeCsvCell).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'metrics.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    }, [data]);

    return (
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems={'center'}
          spacing={2}
          sx={{ px: 1, py: 1.1, pt: 0.0 }}
        >
          <div>{title && <h3 style={{ marginLeft: '10px' }}>{title}</h3>}</div>
          {showCopyButton || showDownloadCsvButton ? (
            <Stack direction='row' spacing={1}>
              {showDownloadCsvButton ? (
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<ContentCopyIcon />}
                  onClick={handleDownloadCsv}
                >
                  Download csv
                </Button>
              ) : null}
              {showCopyButton ? (
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={handleCopyMarkdown}
                >
                  {copied ? 'Copied' : 'Copy table'}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ height: 32 }}>
                {Object.keys(data[0]).map((key: any, index: any) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontSize: '0.8rem',
                      lineHeight: '1.0rem',
                      py: 1.25,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <strong>{key}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((job: any, index: any) => (
                <TableRow hover={true} key={index} sx={{ height: 32 }}>
                  {Object.values(job).map((value: any, i: number) => (
                    <TableCell
                      key={i}
                      sx={{
                        py: 0.5,
                        px: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
);
